import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { format } from 'date-fns';

import CustomBackdrop from '../../components/CustomBackdrop/CustomBackdrop';
import { GDriveBulkConfigSection } from '../../components/GDriveBulkConfigSection';
import PageHeader from '../../components/PageHeader';
import {
  useCortexConfigQuery,
  useUpdateCortexConfigMutation,
  useUploadFileMutation,
  useRegenerateAllDAsMutation,
  useEnvironmentConfigurationQuery,
  useScheduledTasksEnabledQuery,
} from '../../gql/generated/graphql';
import { toSnakeCase } from '../../helpers/helpers';
import { useSnackbar } from '../../providers/SnackbarProvider';
import CustomTextField from '../ValidationPage/components/CustomTextField/CustomTextField';

type FormData = {
  postToZapier: boolean;
  aiValidationOverwriteEnabled: boolean;
  maxRecursionDepth: number | null;
  mongoSiteCacheInDays: number | null;
  minFacesForGroupPicture: number | null;
  maxCrawledPagesPerDomain: number | null;
  macAcceptableWebsitePopularity: number | null;
  assessmentXlsUploadTime: string;
  assessmentXlsTemplateId: string;
  initialBbWeightForSourcescrubTags: number | null;
  pipedriveDfmTargetDealsFilterId: number | null;
  pipedriveOpenTargetDealsLimit: number | null;
  pipedriveSoftwareCompanyOrgTypeIndicator: number | null;
  pipedriveCortexLabelId: number | null;
  pipedriveHotLeadLabelId: number | null;
  sourcescrubTagIdForGenericGenericCompanyLabel: number | null;
  scheduledCrawlerBatchSize: number | null;
  v2RecrawlBatchSize: number | null;
};

const HARDCODED_COMPANY_ID = -1;

const SettingsPage = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    postToZapier: false,
    aiValidationOverwriteEnabled: false,
    maxRecursionDepth: null,
    mongoSiteCacheInDays: null,
    minFacesForGroupPicture: null,
    maxCrawledPagesPerDomain: null,
    macAcceptableWebsitePopularity: null,
    assessmentXlsUploadTime: '',
    assessmentXlsTemplateId: '',

    initialBbWeightForSourcescrubTags: null,
    pipedriveDfmTargetDealsFilterId: null,
    pipedriveOpenTargetDealsLimit: null,
    pipedriveSoftwareCompanyOrgTypeIndicator: null,
    pipedriveCortexLabelId: null,
    pipedriveHotLeadLabelId: null,
    sourcescrubTagIdForGenericGenericCompanyLabel: null,
    scheduledCrawlerBatchSize: null,
    v2RecrawlBatchSize: null,
  });

  const { data, loading, refetch: refetchCortexConfig } = useCortexConfigQuery();
  const { data: envData, loading: envLoading } = useEnvironmentConfigurationQuery();
  const { data: cronData, loading: cronLoading } = useScheduledTasksEnabledQuery();
  const [uploadFile, { loading: uploadFileLoading }] = useUploadFileMutation();
  const [updateCortexConfig, { loading: updateCortexConfigLoading }] = useUpdateCortexConfigMutation();
  const [regenerateAllDAs, { loading: regenerateAllDAsLoading }] = useRegenerateAllDAsMutation();

  useEffect(() => {
    if (data?.cortexConfig) {
      const convertedData: FormData = {
        postToZapier: data.cortexConfig.postToZapier ?? false,
        aiValidationOverwriteEnabled: data.cortexConfig.aiValidationOverwriteEnabled ?? false,
        maxRecursionDepth: data.cortexConfig.maxRecursionDepth ?? null,
        mongoSiteCacheInDays: data.cortexConfig.mongoSiteCacheInDays ?? null,
        minFacesForGroupPicture: data.cortexConfig.minFacesForGroupPicture ?? null,
        maxCrawledPagesPerDomain: data.cortexConfig.maxCrawledPagesPerDomain ?? null,
        macAcceptableWebsitePopularity: data.cortexConfig.macAcceptableWebsitePopularity ?? null,
        assessmentXlsUploadTime: data.cortexConfig.assessmentXlsUploadTime ?? '',
        assessmentXlsTemplateId: data.cortexConfig.assessmentXlsTemplateId ?? '',

        initialBbWeightForSourcescrubTags: data.cortexConfig.initialBbWeightForSourcescrubTags ?? null,
        pipedriveDfmTargetDealsFilterId: data.cortexConfig.pipedriveDfmTargetDealsFilterId ?? null,
        pipedriveOpenTargetDealsLimit: data.cortexConfig.pipedriveOpenTargetDealsLimit ?? null,
        pipedriveSoftwareCompanyOrgTypeIndicator: data.cortexConfig.pipedriveSoftwareCompanyOrgTypeIndicator ?? null,
        pipedriveCortexLabelId: data.cortexConfig.pipedriveCortexLabelId ?? null,
        pipedriveHotLeadLabelId: data.cortexConfig.pipedriveHotLeadLabelId ?? null,
        sourcescrubTagIdForGenericGenericCompanyLabel:
          data.cortexConfig.sourcescrubTagIdForGenericGenericCompanyLabel ?? null,
        scheduledCrawlerBatchSize: data.cortexConfig.scheduledCrawlerBatchSize ?? null,
        v2RecrawlBatchSize: data.cortexConfig.v2RecrawlBatchSize ?? null,
      };
      setFormData(convertedData);
    }
  }, [data]);

  const handleChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field: keyof FormData, value: any) => {
    if (!isNaN(value)) {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const convertedAttributes = Object.keys(formData).reduce(
      (acc, key) => {
        const snakeKey = toSnakeCase(key);
        acc[snakeKey] = formData[key as keyof FormData];
        return acc;
      },
      {} as Record<string, any>
    );
    const convertedAttributesStringified = JSON.stringify(convertedAttributes);

    const response = await updateCortexConfig({
      variables: {
        attributes: convertedAttributesStringified,
      },
    });

    if (response.data.updateCortexConfig.ok) {
      refetchCortexConfig();
      showSnackbar(t('settingsPage.successMessage'), 'success', false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      setUploadError(t('settingsPage.noFileSelectedError'));
      return;
    }

    const reader = new FileReader();

    reader.onload = async () => {
      try {
        const base64File = (reader.result as string).split(',')[1];

        const response = await uploadFile({
          variables: {
            companyId: HARDCODED_COMPANY_ID,
            file: base64File,
            fileName: file.name,
            isBmFile: true,
          },
        });

        if (response.errors) {
          throw new Error(t('settingsPage.uploadFailedError'));
        }

        refetchCortexConfig();
        setUploadError(null);
        showSnackbar(t('settingsPage.uploadSuccessMessage'), 'success', false);
      } catch (error) {
        setUploadError((error as Error).message || t('settingsPage.genericUploadError'));
      }
    };

    reader.onerror = () => {
      setUploadError(t('settingsPage.fileReadError'));
    };

    reader.readAsDataURL(file);
  };

  const handleRegenerateAllDAs = async () => {
    try {
      const response = await regenerateAllDAs();

      if (response.data?.regenerateAllDas?.ok) {
        refetchCortexConfig();
        showSnackbar('All DA files will be regenerated on next access', 'success', false);
      } else {
        throw new Error('Failed to trigger DA regeneration');
      }
    } catch (error) {
      showSnackbar((error as Error).message || 'Error triggering DA regeneration', 'error', false);
    }
  };

  const formatNumberToStringValue = (value: number | null) => {
    return value === null ? '' : String(value);
  };

  const isFormIncomplete = Object.entries(formData).some(([key, value]) => {
    // Skip optional fields
    if (key === 'initialBbWeightForSourcescrubTags') return false;
    if (key === 'scheduledCrawlerBatchSize') return false;
    if (key === 'v2RecrawlBatchSize') return false;
    // Skip boolean fields
    if (typeof value === 'boolean') return false;
    // For string fields, check if empty
    if (typeof value === 'string') return value === '';
    // For number fields, check if null
    return value === null;
  });

  const assessmentUploadTime = formData?.assessmentXlsUploadTime
    ? format(new Date(formData.assessmentXlsUploadTime), 'dd.MM.yyyy HH:mm')
    : '';

  return (
    <Box>
      <PageHeader title="Settings" subtitle="Configure system settings and preferences" variant="default" />
      <Box display="flex" flexDirection="column" gap={2} p={3}>
        <Typography variant="h5">{t('settingsPage.editConfig')}</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Button variant="contained" component="label">
            {t('settingsPage.uploadFile')}
            <input type="file" hidden onChange={handleFileUpload} />
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleRegenerateAllDAs}
            disabled={regenerateAllDAsLoading}
          >
            Regenerate all DAs
          </Button>
          {uploadError && <Typography color="error">{uploadError}</Typography>}
        </Box>
        <Box component="form" display="flex" flexDirection="column" gap={2} mt={4} onSubmit={handleSubmit}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.postToZapier}
                onChange={e => handleChange('postToZapier', e.target.checked)}
              />
            }
            label={t('settingsPage.postToZapier')}
          />
          <FormControlLabel
            control={<Checkbox checked={cronData?.scheduledTasksEnabled ?? false} disabled />}
            label="Execute cron jobs"
            sx={{ opacity: 0.6 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.aiValidationOverwriteEnabled}
                onChange={e => handleChange('aiValidationOverwriteEnabled', e.target.checked)}
              />
            }
            label="Enable AI Validation to Overwrite Existing Data"
          />
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <CustomTextField
                name="maxRecursionDepth"
                label={t('settingsPage.maxRecursionDepth')}
                type="number"
                value={formatNumberToStringValue(formData.maxRecursionDepth)}
                onChange={e => handleNumberChange('maxRecursionDepth', e.target.value)}
              />
              <CustomTextField
                name="mongoSiteCacheInDays"
                label={t('settingsPage.mongoSiteCacheInDays')}
                type="number"
                value={formatNumberToStringValue(formData.mongoSiteCacheInDays)}
                onChange={e => handleNumberChange('mongoSiteCacheInDays', e.target.value)}
              />
              <CustomTextField
                name="minFacesForGroupPicture"
                label={t('settingsPage.minFacesForGroupPicture')}
                type="number"
                value={formatNumberToStringValue(formData.minFacesForGroupPicture)}
                onChange={e => handleNumberChange('minFacesForGroupPicture', e.target.value)}
              />
              <CustomTextField
                name="maxCrawledPagesPerDomain"
                label={t('settingsPage.maxCrawledPagesPerDomain')}
                type="number"
                value={formatNumberToStringValue(formData.maxCrawledPagesPerDomain)}
                onChange={e => handleNumberChange('maxCrawledPagesPerDomain', e.target.value)}
              />
              <CustomTextField
                name="macAcceptableWebsitePopularity"
                label={t('settingsPage.macAcceptableWebsitePopularity')}
                type="number"
                value={formatNumberToStringValue(formData.macAcceptableWebsitePopularity)}
                onChange={e => handleNumberChange('macAcceptableWebsitePopularity', e.target.value)}
              />
              <CustomTextField
                name="assessmentXlsUploadTime"
                label={t('settingsPage.assessmentXlsUploadTime')}
                disabled
                value={assessmentUploadTime}
                onChange={e => handleChange('assessmentXlsUploadTime', e.target.value)}
              />
              <CustomTextField
                name="assessmentXlsTemplateId"
                label={t('settingsPage.assessmentXlsTemplateId')}
                disabled
                value={formData.assessmentXlsTemplateId}
                onChange={e => handleChange('assessmentXlsTemplateId', e.target.value)}
              />
              <CustomTextField
                name="initialBbWeightForSourcescrubTags"
                label={t('settingsPage.initialBbWeightForSourcescrubTags')}
                type="number"
                inputProps={{
                  step: '0.1',
                  min: '0',
                  max: '1',
                }}
                value={formatNumberToStringValue(formData.initialBbWeightForSourcescrubTags)}
                onChange={e => {
                  const value = parseFloat(e.target.value);
                  if (isNaN(value)) {
                    handleNumberChange('initialBbWeightForSourcescrubTags', null);
                  } else if (value >= 0 && value <= 1) {
                    handleNumberChange('initialBbWeightForSourcescrubTags', value);
                  }
                }}
              />
              <CustomTextField
                name="pipedriveDfmTargetDealsFilterId"
                label={t('settingsPage.pipedriveDfmTargetDealsFilterId')}
                type="number"
                value={formatNumberToStringValue(formData.pipedriveDfmTargetDealsFilterId)}
                onChange={e => handleNumberChange('pipedriveDfmTargetDealsFilterId', e.target.value)}
              />
              <CustomTextField
                name="pipedriveOpenTargetDealsLimit"
                label={t('settingsPage.pipedriveOpenTargetDealsLimit')}
                type="number"
                value={formatNumberToStringValue(formData.pipedriveOpenTargetDealsLimit)}
                onChange={e => handleNumberChange('pipedriveOpenTargetDealsLimit', e.target.value)}
              />
              <CustomTextField
                name="pipedriveSoftwareCompanyOrgTypeIndicator"
                label={t('settingsPage.pipedriveSoftwareCompanyOrgTypeIndicator')}
                type="number"
                value={formatNumberToStringValue(formData.pipedriveSoftwareCompanyOrgTypeIndicator)}
                onChange={e => handleNumberChange('pipedriveSoftwareCompanyOrgTypeIndicator', e.target.value)}
              />
              <CustomTextField
                name="pipedriveCortexLabelId"
                label={t('settingsPage.pipedriveCortexLabelId')}
                type="number"
                value={formatNumberToStringValue(formData.pipedriveCortexLabelId)}
                onChange={e => handleNumberChange('pipedriveCortexLabelId', e.target.value)}
              />
              <CustomTextField
                name="pipedriveHotLeadLabelId"
                label={t('settingsPage.pipedriveHotLeadLabelId')}
                type="number"
                value={formatNumberToStringValue(formData.pipedriveHotLeadLabelId)}
                onChange={e => handleNumberChange('pipedriveHotLeadLabelId', e.target.value)}
              />
              <CustomTextField
                name="sourcescrubTagIdForGenericGenericCompanyLabel"
                label={t('settingsPage.sourcescrubTagIdForGenericGenericCompanyLabel')}
                type="number"
                value={formatNumberToStringValue(formData.sourcescrubTagIdForGenericGenericCompanyLabel)}
                onChange={e => handleNumberChange('sourcescrubTagIdForGenericGenericCompanyLabel', e.target.value)}
              />
              <CustomTextField
                name="scheduledCrawlerBatchSize"
                label={t('settingsPage.scheduledCrawlerBatchSize')}
                type="number"
                value={formatNumberToStringValue(formData.scheduledCrawlerBatchSize)}
                onChange={e => handleNumberChange('scheduledCrawlerBatchSize', e.target.value)}
              />
              <CustomTextField
                name="v2RecrawlBatchSize"
                label={t('settingsPage.v2RecrawlBatchSize')}
                type="number"
                value={formatNumberToStringValue(formData.v2RecrawlBatchSize)}
                onChange={e => handleNumberChange('v2RecrawlBatchSize', e.target.value)}
              />
              <Button type="submit" variant="contained" color="primary" disabled={isFormIncomplete}>
                {t('settingsPage.updateConfig')}
              </Button>
            </>
          )}
        </Box>

        {/* Environment Configuration Section */}
        <Box mt={4} p={3} sx={{ backgroundColor: 'var(--surface-raised)', borderRadius: 1 }}>
          <Typography variant="h5" mb={2}>
            Environment Configuration
          </Typography>
          {envLoading ? (
            <CircularProgress size={24} />
          ) : envData?.environmentConfiguration ? (
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2">
                <strong>Doppler Project:</strong> {envData.environmentConfiguration.dopplerProject || 'Not set'}
              </Typography>
              <Typography variant="body2">
                <strong>Doppler Config:</strong> {envData.environmentConfiguration.dopplerConfig || 'Not set'}
              </Typography>
              <Typography variant="body2">
                <strong>Database Revision:</strong> {envData.environmentConfiguration.alembicRevision || 'Unknown'}
              </Typography>
              <Typography variant="body2">
                <strong>Latest Migration:</strong>{' '}
                {envData.environmentConfiguration.latestMigrationVersion || 'Unknown'}
              </Typography>
              <Typography variant="body2">
                <strong>Steps behind latest migration:</strong>{' '}
                {envData.environmentConfiguration.stepsBehindLatestMigration !== null
                  ? envData.environmentConfiguration.stepsBehindLatestMigration
                  : 'Unknown'}
              </Typography>
              <Typography variant="body2">
                <strong>Git Branch:</strong> {envData.environmentConfiguration.gitBranch || 'Unknown'}
              </Typography>
              <Typography variant="body2">
                <strong>Git Commit:</strong>{' '}
                {envData.environmentConfiguration.gitCommit
                  ? envData.environmentConfiguration.gitCommit.substring(0, 8)
                  : 'Unknown'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="error">
              Failed to load environment configuration
            </Typography>
          )}
        </Box>

        {/* GDrive Bulk Configuration Section */}
        <GDriveBulkConfigSection />

        <CustomBackdrop open={updateCortexConfigLoading || uploadFileLoading || regenerateAllDAsLoading} />
      </Box>
    </Box>
  );
};

export default SettingsPage;
